import {
  Action,
  Card,
  EmptyStatus,
  IconButton,
  openDialog,
  showNotification,
} from 'platform/components';
import {Show} from 'platform/foundation';
import {useFormatCurrency} from 'platform/locale';
import {match} from 'ts-pattern';

import {MouseEvent} from 'react';

import {always} from 'ramda';
import {isNilOrEmpty, noop} from 'ramda-adjunct';

import {
  useAddOfferAlternativeUpsellFromCatalogMutation,
  useUpdateOfferAlternativeMutation,
} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {EMPTY_PLACEHOLDER, usePricePermissions} from '@dms/shared';
import {type ColumnType, SimpleTable} from '@dms/teas';

import {buildArray, getRandomId, Nullish} from 'shared/web';

import {AlternativePriceBox} from './AlternativePriceBox';
import {AlternativeUpsellCatalogDialog} from './AlternativeUpsellCatalogDialog';
import {AlternativeUpsellDialog, type AlternativeUpsellFormValues} from './AlternativeUpsellDialog';
import {type OfferAlternativeWithRights} from './getAlternativeRights';
import {handleOfferAlternativeError, isCurrencyMismatchError} from './handleOfferAlternativeError';
import {
  type OfferAlternativeUpsellLine,
  type OfferAlternativeUpsellPatchLine,
} from './OfferAlternative';

type UpsellPatchLine = OfferAlternativeUpsellPatchLine;

const toAmount = (value: string | number | Nullish) => (isNilOrEmpty(value) ? null : String(value));

const isSameAmount = (value: string | null, stored: string | Nullish) =>
  isNilOrEmpty(stored) ? value === null : value !== null && Number(value) === Number(stored);

// The backend keeps every field a line omits, so an untouched line travels as its id alone and
// never echoes a price the user may not see.
const toPatchLine = (line: OfferAlternativeUpsellLine): UpsellPatchLine => ({id: line.id});

const getPurchasePriceChange = (value: string | null, stored: string | Nullish) =>
  match(value)
    .when((amount) => isSameAmount(amount, stored), always({}))
    .with(null, always({clear: ['purchasePriceWithoutVat' as const]}))
    .otherwise((amount) => ({purchasePriceWithoutVat: amount}));

const toEditedLine = (
  values: AlternativeUpsellFormValues,
  line: OfferAlternativeUpsellLine
): UpsellPatchLine => {
  const sellingPrice = toAmount(values.sellingPriceWithVat);

  return {
    id: line.id,
    ...(values.name === line.name ? {} : {name: values.name}),
    // An empty field of a hidden selling price is no change; the stored price stays.
    ...(sellingPrice === null || isSameAmount(sellingPrice, line.sellingPrice?.withVat.amount)
      ? {}
      : {sellingPriceWithVat: sellingPrice}),
    ...getPurchasePriceChange(
      toAmount(values.purchasePriceWithoutVat),
      line.purchasePriceWithoutVat?.amount
    ),
  };
};

// The dialog has no VAT control: a new line takes the backend default (standard code and rate).
const toNewLine = (values: AlternativeUpsellFormValues): UpsellPatchLine => ({
  name: values.name,
  sellingPriceWithVat: String(values.sellingPriceWithVat),
  purchasePriceWithoutVat: toAmount(values.purchasePriceWithoutVat),
});

interface AlternativeUpsellCardProps {
  businessCaseId: string;
  alternative: OfferAlternativeWithRights;
}

/**
 * Upsell lines of one alternative; every typed change PATCHes the line list as a new revision, a
 * catalogue product is added by the backend. Adding, editing and removing a line need the same
 * rights as on the deal vehicle's upsell table.
 */
export function AlternativeUpsellCard(props: AlternativeUpsellCardProps) {
  const formatCurrency = useFormatCurrency();
  const [patchAlternative] = useUpdateOfferAlternativeMutation();
  const [addFromCatalogue] = useAddOfferAlternativeUpsellFromCatalogMutation();
  const {terms, canAddUpsell, canAddUpsellFromCatalogue, canEditUpsell, canRemoveUpsell} =
    props.alternative;
  const {canReadBusinessCaseSaleVehiclePurchasePrice: canReadPurchasePrice} = usePricePermissions({
    businessCaseRecordId: props.businessCaseId,
    vehicleRecordId: props.alternative.vehicleId,
  });

  const saveLines = (upsell: UpsellPatchLine[]) =>
    patchAlternative({
      businessCaseId: props.businessCaseId,
      alternativeId: props.alternative.id,
      updateOfferAlternativeRequestBody: {expectedRevision: props.alternative.revision, upsell},
    })
      .unwrap()
      .catch((error: unknown) => {
        handleOfferAlternativeError(error);
        throw error;
      });

  const addProduct = (productId: string) =>
    addFromCatalogue({
      businessCaseId: props.businessCaseId,
      alternativeId: props.alternative.id,
      addOfferAlternativeUpsellFromCatalogRequestBody: {
        productId,
        expectedRevision: props.alternative.revision,
      },
    })
      .unwrap()
      .catch((error: unknown) => {
        if (isCurrencyMismatchError(error)) {
          showNotification.warning(
            i18n.t('entity.offerAlternative.upsell.catalogCurrencyMismatch', {
              defaultValue: 'This product is priced in another currency than the alternative.',
            })
          );
        } else {
          handleOfferAlternativeError(error);
        }
        throw error;
      });

  const openLineDialog = (line?: OfferAlternativeUpsellLine) => {
    const dialogId = `offerAlternativeUpsell-${getRandomId()}`;
    openDialog(
      <AlternativeUpsellDialog
        dialogId={dialogId}
        canReadPurchasePrice={canReadPurchasePrice}
        currency={terms.currency}
        line={line}
        onSubmit={(values) =>
          saveLines(
            line
              ? terms.upsell.map((existing) =>
                  existing.id === line.id ? toEditedLine(values, line) : toPatchLine(existing)
                )
              : [...terms.upsell.map(toPatchLine), toNewLine(values)]
          )
        }
      />,
      {
        id: dialogId,
        title: line
          ? i18n.t('page.businessCase.labels.editUpsellItem')
          : i18n.t('page.businessCase.labels.newUpsellItem'),
      }
    );
  };

  const openCatalogDialog = () => {
    const dialogId = `offerAlternativeUpsellCatalog-${getRandomId()}`;
    openDialog(<AlternativeUpsellCatalogDialog dialogId={dialogId} onSelect={addProduct} />, {
      id: dialogId,
      title: i18n.t('entity.businessCase.actions.addItemFromCatalog'),
      size: 'large',
    });
  };

  const removeLine = (lineId: string) => (event: MouseEvent) => {
    event.stopPropagation();
    // Failures are already reported by saveLines.
    saveLines(terms.upsell.filter((line) => line.id !== lineId).map(toPatchLine)).catch(noop);
  };

  const formatPrice = (amount: string, currency: string) =>
    formatCurrency(Number(amount), currency);

  const actions = buildArray<Action>()
    .when(canAddUpsell, {
      type: 'button',
      variant: 'link',
      leftIcon: 'content/add_circle',
      title: i18n.t('general.actions.addItem'),
      onClick: () => openLineDialog(),
      'data-testid': 'offerAlternativeUpsell-add',
    })
    .when(canAddUpsellFromCatalogue, {
      type: 'button',
      variant: 'link',
      leftIcon: 'content/add_circle',
      title: i18n.t('entity.businessCase.actions.addItemFromCatalog'),
      onClick: openCatalogDialog,
      'data-testid': 'offerAlternativeUpsell-addFromCatalog',
    });

  return (
    <Card
      title={i18n.t('entity.businessCase.labels.upsell')}
      actions={actions}
      variant="inlineWhite"
      data-testid="offerAlternativeUpsell"
    >
      {terms.upsell.length > 0 ? (
        <SimpleTable
          highlightRowOnHover={canEditUpsell}
          showRowDivider
          columns={buildArray<ColumnType>()
            .add({id: 'title', Header: i18n.t('entity.person.labels.title')})
            .when(canReadPurchasePrice, {
              id: 'purchasePrice',
              Header: i18n.t('entity.offerAlternative.upsell.purchasePriceWithoutVat', {
                defaultValue: 'Purchase price excl. VAT',
              }),
              align: 'right',
            })
            .add({
              id: 'sellingPrice',
              Header: i18n.t('general.labels.sellingPrice'),
              align: 'right',
            })
            .add({id: 'action', Header: '', align: 'right'})}
          rows={[
            {
              data: terms.upsell.map((line) => ({
                id: line.id,
                title: line.name,
                purchasePrice: line.purchasePriceWithoutVat
                  ? formatPrice(
                      line.purchasePriceWithoutVat.amount,
                      line.purchasePriceWithoutVat.currency
                    )
                  : EMPTY_PLACEHOLDER,
                // Same price/without-VAT pair as the legacy upsell total.
                sellingPrice: (
                  <AlternativePriceBox size="small" align="right" price={line.sellingPrice} />
                ),
                action: (
                  <Show when={canRemoveUpsell}>
                    <IconButton
                      icon="action/delete"
                      severity="danger"
                      onClick={removeLine(line.id)}
                    />
                  </Show>
                ),
              })),
            },
          ]}
          onRowClick={(row) => {
            const line = terms.upsell.find((candidate) => candidate.id === row.id);
            if (canEditUpsell && line) {
              openLineDialog(line);
            }
          }}
        />
      ) : (
        <EmptyStatus
          variant="table"
          headline={i18n.t('entity.businessCase.notifications.noUpsellProducts')}
        />
      )}
    </Card>
  );
}
